// Extracted from src/pages/staff/ZoneForm.js (Link Exhibits to Zone section)
// in PR 12 ST 5. Zone-specific — no analog in features/exhibits/.
// Presentational + controlled: orchestrator (ST 6) owns selectedExhibitIds
// and provides the toggle callback. Exhibit list itself is fetched via
// useExhibitList from @shared/api/hooks/useExhibits and passed in as
// `allExhibits` so this component stays pure render.
//
// #4: with many exhibits the flat grid was hard to work with, so the list
// carries local-only view state — a search box (title TC/EN/SC + code) and an
// All / Linked / Available / Taken filter, plus a prominent "N linked" count.
// These are pure presentation (they never touch the saved exhibit_ids), so
// they live here rather than in the orchestrator.
//
// Available vs Taken (ADR-0025 single-parent): each ExhibitListItem carries the
// authoritative `zone_id` / `zone_title_tc` of its holding zone. An exhibit in
// ANOTHER zone is "Taken" — greyed out and non-selectable here, since linking it
// would 409 at save. An exhibit in no zone (or in THIS zone) is selectable.

import { useMemo, useState, type FC } from "react";
import { Ban, Check, Search } from "lucide-react";
import { Input } from "@shared/components/ui/input";
import { assetUrl } from "@shared/api/assets";
import type { ExhibitListItem } from "@shared/api/exhibits";

type FilterMode = "all" | "linked" | "available" | "taken";

export interface ExhibitsListProps {
  allExhibits: ExhibitListItem[];
  selectedExhibitIds: string[];
  onToggle: (exhibitId: string) => void;
  /**
   * The id of the zone being edited (undefined when creating). An exhibit whose
   * `zone_id` equals this is held by THIS zone, so it's selectable, not "Taken".
   */
  currentZoneId?: string;
  /**
   * Linked exhibits that are purged (tombstones) — never selectable here, shown
   * read-only as a record of what the zone once contained (#5).
   */
  purgedLinked?: ExhibitListItem[];
}

function buildImageSrc(coverImageId: string | null | undefined): string | null {
  return coverImageId ? assetUrl(coverImageId) : null;
}

// Same state glyphs as the dashboards — the picker doubles as a publish
// triage surface (batch imports land as draft and are invisible to
// visitors until published).
function stateLabel(state: ExhibitListItem["state"]): string {
  if (state === "published") return "▣ published";
  if (state === "archived") return "▼ archived";
  if (state === "purged") return "✕ purged";
  return "◌ draft";
}

const ExhibitsList: FC<ExhibitsListProps> = ({
  allExhibits,
  selectedExhibitIds,
  onToggle,
  currentZoneId,
  purgedLinked = [],
}) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterMode>("all");

  // Held by a *different* zone than the one being edited → not linkable here.
  const isTakenElsewhere = (e: ExhibitListItem): boolean =>
    Boolean(e.zoneId) && e.zoneId !== currentZoneId;

  // Count only selections still present in the selectable grid; purged links
  // (shown separately, read-only) shouldn't inflate the active count.
  const linkedCount = allExhibits.filter((e) =>
    selectedExhibitIds.includes(e.id),
  ).length;

  // The grid shows exhibits matching the active filter AND the search query.
  // The taken check is inlined (not the render helper) so exhaustive-deps is
  // satisfied by the listed primitives alone.
  const visibleExhibits = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allExhibits.filter((e) => {
      const isLinked = selectedExhibitIds.includes(e.id);
      const taken =
        !isLinked && Boolean(e.zoneId) && e.zoneId !== currentZoneId;
      if (filter === "linked" && !isLinked) return false;
      if (filter === "available" && (isLinked || taken)) return false;
      if (filter === "taken" && !taken) return false;
      if (q) {
        const haystack =
          `${e.code} ${e.titleTc} ${e.titleEn} ${e.titleSc}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allExhibits, selectedExhibitIds, filter, query, currentZoneId]);

  const filterChips: { mode: FilterMode; label: string }[] = [
    { mode: "all", label: "All" },
    { mode: "linked", label: `Linked · ${linkedCount}` },
    { mode: "available", label: "Available" },
    { mode: "taken", label: "Taken" },
  ];

  return (
    <div
      className="space-y-4 p-6 bg-white border border-border rounded-lg"
      data-testid="exhibits-list-section"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold">Link Exhibits to Zone</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Select exhibits to include in this zone.
          </p>
        </div>
        <span
          className="shrink-0 inline-flex items-center rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-medium"
          data-testid="linked-count"
        >
          {linkedCount} linked
        </span>
      </div>

      {allExhibits.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No exhibits available. Create some exhibits first.
        </div>
      ) : (
        <>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title or code…"
                aria-label="Search exhibits"
                className="pl-9"
                data-testid="exhibit-search"
              />
            </div>
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label="Filter exhibits"
            >
              {filterChips.map((chip) => {
                const active = filter === chip.mode;
                return (
                  <button
                    key={chip.mode}
                    type="button"
                    onClick={() => setFilter(chip.mode)}
                    aria-pressed={active}
                    className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                      active
                        ? "border-primary bg-primary text-white"
                        : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                    data-testid={`exhibit-filter-${chip.mode}`}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>

          {visibleExhibits.length === 0 ? (
            <div
              className="text-center py-8 text-muted-foreground"
              data-testid="exhibits-empty-filtered"
            >
              No exhibits match your search or filter.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {visibleExhibits.map((exhibit) => {
                const isSelected = selectedExhibitIds.includes(exhibit.id);
                const taken = !isSelected && isTakenElsewhere(exhibit);
                const imgSrc = buildImageSrc(exhibit.coverImageId);
                return (
                  // Split interactions: the NAME opens the exhibit form in a
                  // new tab (so unsaved zone edits survive the round-trip);
                  // only the CIRCLE toggles the link. The card body itself is
                  // inert — no more accidental link-toggles.
                  <div
                    key={exhibit.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      taken
                        ? "border-border bg-muted/40 opacity-60"
                        : isSelected
                          ? "border-primary bg-primary/5"
                          : "border-border"
                    }`}
                    data-testid={`exhibit-row-${exhibit.id}`}
                  >
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={exhibit.titleTc}
                        className="w-12 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                        No img
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <a
                        href={`/staff/exhibit/${exhibit.id}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-sm truncate block hover:underline hover:text-primary"
                        title="Open the exhibit form in a new tab"
                        data-testid={`exhibit-open-${exhibit.id}`}
                      >
                        {exhibit.titleTc}
                      </a>
                      <div className="text-xs text-muted-foreground">
                        Code: {exhibit.code}
                        <span
                          className={`ml-2 ${
                            exhibit.state === "published"
                              ? "text-green-700"
                              : "text-amber-700"
                          }`}
                          data-testid={`exhibit-state-${exhibit.id}`}
                        >
                          {stateLabel(exhibit.state)}
                        </span>
                      </div>
                      {taken && (
                        <div
                          className="text-xs text-amber-700 mt-0.5 truncate"
                          data-testid={`exhibit-taken-tag-${exhibit.id}`}
                        >
                          In {exhibit.zoneTitleTc ?? "another zone"}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={taken}
                      aria-disabled={taken}
                      aria-pressed={isSelected}
                      aria-label={
                        isSelected
                          ? `Unlink ${exhibit.titleTc}`
                          : `Link ${exhibit.titleTc}`
                      }
                      onClick={taken ? undefined : () => onToggle(exhibit.id)}
                      className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
                        taken
                          ? "border-muted-foreground/20 cursor-not-allowed"
                          : isSelected
                            ? "border-primary bg-primary text-white cursor-pointer"
                            : "border-muted-foreground/30 cursor-pointer hover:border-primary"
                      }`}
                      data-testid={`exhibit-select-${exhibit.id}`}
                    >
                      {taken ? (
                        <Ban className="w-3.5 h-3.5 text-muted-foreground/60" />
                      ) : (
                        isSelected && <Check className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {purgedLinked.length > 0 && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm"
          data-testid="purged-linked-exhibits"
        >
          <p className="font-medium text-amber-900">
            {purgedLinked.length} linked{" "}
            {purgedLinked.length === 1 ? "exhibit is" : "exhibits are"} purged
          </p>
          <ul className="mt-1 space-y-0.5 text-amber-800">
            {purgedLinked.map((e) => (
              <li key={e.id} data-testid={`purged-linked-${e.id}`}>
                {e.code} — {e.titleTc}{" "}
                <span className="text-amber-600">(assets removed)</span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-amber-700">
            Kept as a record of what this zone once contained; they don't count
            toward the zone's live exhibits and can't be re-selected.
          </p>
        </div>
      )}
    </div>
  );
};

export default ExhibitsList;
